import { useMemo, useState } from 'react'
import Modal from '@/components/ui/Modal'
import { SlotPickerSection } from '@/components/ui/SlotPickerSection'
import { ModalFooter } from '@/components/ui/ConfirmModal'
import { useAvailableTimeSlots } from '@/api/hooks/appointment-slots.hooks'
import { useRescheduleAppointment } from '@/api/hooks/appointments.hooks'
import { toastCallbacks } from '@/api/hooks/useMutationWithToast'
import { formatDateKey } from '@/utils/date'
import { groupSlotsByDateKey, findNextAvailableDate } from '@/utils/appointments'
import type { AvailableTimeSlotsDto } from '@/api/contracts/appointment-slots.contract'

type RescheduleModalProps = {
  appointmentId: string
  specialistSlug: string
  onClose: () => void
}

export function RescheduleModal({ appointmentId, specialistSlug, onClose }: RescheduleModalProps) {
  const reschedule = useRescheduleAppointment()
  const { data: allSlots = [], isLoading } = useAvailableTimeSlots({ slug: specialistSlug })

  const [selectedDate, setSelectedDate] = useState<Date | undefined>()
  const [selectedSlot, setSelectedSlot] = useState<AvailableTimeSlotsDto | undefined>()

  const grouped = useMemo(() => groupSlotsByDateKey(allSlots), [allSlots])
  const availableDates = useMemo(() => Object.keys(grouped), [grouped])
  const slotsForDate = useMemo(() => (selectedDate ? grouped[formatDateKey(selectedDate)] || [] : []), [grouped, selectedDate])
  const inPersonSlots = useMemo(() => slotsForDate.filter((s) => s.locationId !== null), [slotsForDate])
  const telemedicineSlots = useMemo(() => slotsForDate.filter((s) => s.locationId === null), [slotsForDate])
  const nextAvailableDate = useMemo(() => findNextAvailableDate(availableDates), [availableDates])

  const handleDateSelect = (d: Date | undefined) => { setSelectedDate(d); setSelectedSlot(undefined) }
  const handleSlotSelect = (slot: AvailableTimeSlotsDto) => setSelectedSlot(slot)

  const handleConfirm = () => {
    if (!selectedSlot) return
    reschedule.mutate(
      { appointmentId, newDate: selectedSlot.dateTime },
      toastCallbacks({ onSuccess: 'Rescheduled', onSuccessFn: onClose })
    )
  }

  return (
    <Modal isOpen onClose={onClose} title='Reschedule Appointment'>
      <div className='space-y-6'>
        <SlotPickerSection
          selectedDate={selectedDate}
          onDateSelect={handleDateSelect}
          availableDates={availableDates}
          slotsForDate={slotsForDate}
          inPersonSlots={inPersonSlots}
          telemedicineSlots={telemedicineSlots}
          isLoading={isLoading}
          selectedSlot={selectedSlot}
          onSlotSelect={handleSlotSelect}
          nextAvailableDate={nextAvailableDate}
        />
        <ModalFooter
          secondaryLabel='Cancel'
          onClose={onClose}
          primaryLabel='Confirm Reschedule'
          onPrimary={handleConfirm}
          primaryDisabled={!selectedSlot}
        />
      </div>
    </Modal>
  )
}
